import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CalendarRange,
  Globe,
  Image as IconoDeImagen,
  Link2,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { ErrorDeApi } from '@/shared/api/problema';
import { Cargando } from '@/shared/ui/Cargando';
import { ConfirmarEliminacion } from '@/shared/ui/ConfirmarEliminacion';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Interruptor } from '@/shared/ui/Interruptor';
import { AccionesDelFormulario, Campo, Modal, claseEntrada } from '@/shared/ui/Modal';
import { notificar } from '@/shared/ui/avisos';
import { useSesion } from '@/shared/sesion/SesionProvider';
import { PAGINAS_DE_LA_VENTANILLA, type BannerDelPortal, type DatosDeBanner } from './contrato';
import { avisoDeMedidas, enPeso, prepararImagenes, type ImagenesPreparadas } from './imagenes';
import { PestanasDelPortal } from './PestanasDelPortal';

const CLAVE = ['portal', 'banners'];

export function PaginaBanners() {
  const cliente = useQueryClient();
  const { puede } = useSesion();
  const [editando, setEditando] = useState<BannerDelPortal | null>(null);
  const [creando, setCreando] = useState(false);
  const [aEliminar, setAEliminar] = useState<BannerDelPortal | null>(null);

  const banners = useQuery({
    queryKey: CLAVE,
    queryFn: () => api.get<BannerDelPortal[]>('/portal/banners'),
  });
  const lista = banners.data ?? [];

  function refrescar() {
    void cliente.invalidateQueries({ queryKey: CLAVE });
  }

  function alFallar(causa: unknown, respaldo: string) {
    notificar.error(causa instanceof ErrorDeApi ? causa.message : respaldo);
  }

  const publicar = useMutation({
    mutationFn: (b: BannerDelPortal) =>
      api.put<BannerDelPortal>(`/portal/banners/${b.id}`, {
        ...datosDe(b),
        publicado: !b.publicado,
      }),
    onSuccess: refrescar,
    onError: (causa) => alFallar(causa, 'No se pudo cambiar la publicación'),
  });

  const mover = useMutation({
    mutationFn: (ids: number[]) => api.put<BannerDelPortal[]>('/portal/banners/orden', { ids }),
    onSuccess: refrescar,
    onError: (causa) => alFallar(causa, 'No se pudo cambiar el orden'),
  });

  const eliminar = useMutation({
    mutationFn: (id: number) => api.delete<void>(`/portal/banners/${id}`),
    onSuccess: () => {
      setAEliminar(null);
      refrescar();
      notificar.exito('Banner eliminado');
    },
    onError: (causa) => alFallar(causa, 'No se pudo eliminar'),
  });

  function moverA(indice: number, destino: number) {
    const ids = lista.map((b) => b.id);
    const [quitado] = ids.splice(indice, 1);
    if (quitado !== undefined) {
      ids.splice(destino, 0, quitado);
      mover.mutate(ids);
    }
  }

  return (
    <>
      <EncabezadoDePagina
        icono={IconoDeImagen}
        color="esmeralda"
        titulo="Portal ciudadano"
        subtitulo="Los banners del carrusel de la portada"
        acciones={
          puede('portal.crear') && (
            <button
              type="button"
              onClick={() => setCreando(true)}
              title="Nuevo banner"
              aria-label="Nuevo banner"
              className="grid size-11 place-items-center rounded-xl bg-marca-600 text-white shadow-sm transition-colors hover:bg-marca-700"
            >
              <Plus className="size-5" aria-hidden />
            </button>
          )
        }
      />

      <PestanasDelPortal />

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <Globe className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">Medida ideal: 1942 × 809 píxeles.</span> La imagen se
          convierte a WebP aquí mismo, en dos tamaños, antes de subirla. Mientras no haya ningún
          banner publicado, la ventanilla enseña los que trae de fábrica.
        </p>
      </div>

      {banners.isPending ? (
        <Cargando titulo="Cargando banners" detalle="Obteniendo registros" alto="h-48" />
      ) : lista.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-armazon-300 bg-white p-10 text-center text-sm uppercase text-armazon-500">
          Todavía no hay banners. La ventanilla enseña los de fábrica hasta que suba el primero.
        </p>
      ) : (
        <div className="space-y-4">
          {lista.map((b, i) => (
            <article
              key={b.id}
              className="flex flex-col gap-4 rounded-2xl border border-armazon-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center"
            >
              <div className="flex shrink-0 items-center gap-3">
                <div className="flex flex-col items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => moverA(i, i - 1)}
                    disabled={i === 0 || mover.isPending || !puede('portal.editar')}
                    title="Subir"
                    aria-label={`Subir ${b.texto}`}
                    className="rounded p-0.5 text-armazon-400 hover:bg-armazon-100 hover:text-armazon-700 disabled:opacity-30"
                  >
                    <ArrowUp className="size-3.5" aria-hidden />
                  </button>
                  <span className="importe grid size-7 place-items-center rounded-lg bg-armazon-100 text-xs font-semibold text-armazon-600">
                    {i + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => moverA(i, i + 1)}
                    disabled={i === lista.length - 1 || mover.isPending || !puede('portal.editar')}
                    title="Bajar"
                    aria-label={`Bajar ${b.texto}`}
                    className="rounded p-0.5 text-armazon-400 hover:bg-armazon-100 hover:text-armazon-700 disabled:opacity-30"
                  >
                    <ArrowDown className="size-3.5" aria-hidden />
                  </button>
                </div>
                <Miniatura banner={b} />
              </div>

              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-armazon-900">{b.texto}</p>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-armazon-500">
                  <Link2 className="size-3.5 shrink-0" aria-hidden />
                  {nombreDelEnlace(b.enlace)}
                </p>
                <p className="mt-0.5 flex items-center gap-1.5 text-xs text-armazon-500">
                  <CalendarRange className="size-3.5 shrink-0" aria-hidden />
                  {b.desde || b.hasta
                    ? `${b.desde ?? 'SIN INICIO'} → ${b.hasta ?? 'SIN FIN'}`
                    : 'SIEMPRE'}
                </p>
                <p className="mt-0.5 text-[11px] uppercase text-armazon-400">
                  {b.tipo.replace('image/', '')} · {enPeso(b.pesoGrande)} computadora ·{' '}
                  {enPeso(b.pesoMovil)} teléfono
                </p>
              </div>

              <div className="flex shrink-0 items-center gap-4">
                <div className="flex w-24 flex-col items-center gap-1">
                  <Interruptor
                    activo={b.publicado}
                    alCambiar={() => publicar.mutate(b)}
                    etiqueta={`Publicar o retirar ${b.texto}`}
                    deshabilitado={publicar.isPending || !puede('portal.editar')}
                  />
                  <span
                    className={`text-[10px] font-semibold uppercase ${b.publicado ? 'text-marca-700' : 'text-armazon-400'}`}
                  >
                    {b.publicado ? 'Publicado' : 'Sin publicar'}
                  </span>
                </div>
                {puede('portal.editar') && (
                  <button
                    type="button"
                    onClick={() => setEditando(b)}
                    title="Editar"
                    aria-label={`Editar ${b.texto}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="size-[15px]" aria-hidden />
                  </button>
                )}
                {puede('portal.eliminar') && (
                  <button
                    type="button"
                    onClick={() => setAEliminar(b)}
                    title="Eliminar"
                    aria-label={`Eliminar ${b.texto}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-red-50 hover:text-alerta"
                  >
                    <Trash2 className="size-[15px]" aria-hidden />
                  </button>
                )}
              </div>
            </article>
          ))}
        </div>
      )}

      <DialogoDeBanner
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        banner={editando}
        siguienteOrden={lista.length + 1}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />

      <ConfirmarEliminacion
        abierto={aEliminar !== null}
        alCerrar={() => setAEliminar(null)}
        alConfirmar={() => aEliminar && eliminar.mutate(aEliminar.id)}
        eliminando={eliminar.isPending}
        titulo="Eliminar banner"
        descripcion="Sale del carrusel y se borran sus imágenes. Para retirarlo sin perderlo, quítele la publicación."
        detalle={aEliminar?.texto}
        error={null}
      />
    </>
  );
}

function datosDe(b: BannerDelPortal): DatosDeBanner {
  return {
    texto: b.texto,
    enlace: b.enlace,
    orden: b.orden,
    publicado: b.publicado,
    desde: b.desde,
    hasta: b.hasta,
  };
}

function nombreDelEnlace(enlace: string | null): string {
  if (!enlace) {
    return 'NO LLEVA A NINGÚN SITIO';
  }
  const pagina = PAGINAS_DE_LA_VENTANILLA.find((p) => p.ruta === enlace);
  return pagina ? `VENTANILLA · ${pagina.nombre.toUpperCase()}` : enlace;
}

function Miniatura({ banner }: { banner: BannerDelPortal }) {
  const imagen = useQuery({
    queryKey: ['portal', 'banner-imagen', banner.id, banner.version],
    queryFn: async () => {
      const blob = await api.descargar(`/portal/banners/${banner.id}/imagen?tamano=movil`);
      return blob ? aUrlDeDatos(blob) : null;
    },
    staleTime: Infinity,
  });
  const url = imagen.data ?? null;

  return (
    <div className="aspect-[1942/809] w-56 shrink-0 overflow-hidden rounded-xl bg-armazon-100 ring-1 ring-armazon-200">
      {url ? (
        <img src={url} alt={banner.texto} className="size-full object-cover" />
      ) : (
        <div className="grid size-full place-items-center text-armazon-300">
          <IconoDeImagen className="size-6" aria-hidden />
        </div>
      )}
    </div>
  );
}

function aUrlDeDatos(imagen: Blob): Promise<string> {
  return new Promise((resolver, rechazar) => {
    const lector = new FileReader();
    lector.onload = () => resolver(String(lector.result));
    lector.onerror = () => rechazar(lector.error);
    lector.readAsDataURL(imagen);
  });
}

const OTRA = '__otra__';

function DialogoDeBanner({
  abierto,
  banner,
  siguienteOrden,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  banner: BannerDelPortal | null;
  siguienteOrden: number;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = banner !== null;
  const enlaceInicial = banner?.enlace ?? '';
  const esDeLaVentanilla =
    enlaceInicial === '' || PAGINAS_DE_LA_VENTANILLA.some((p) => p.ruta === enlaceInicial);

  const [texto, setTexto] = useState(banner?.texto ?? '');
  const [destino, setDestino] = useState(esDeLaVentanilla ? enlaceInicial : OTRA);
  const [externo, setExterno] = useState(esDeLaVentanilla ? '' : enlaceInicial);
  const [desde, setDesde] = useState(banner?.desde ?? '');
  const [hasta, setHasta] = useState(banner?.hasta ?? '');
  const [publicado, setPublicado] = useState(banner?.publicado ?? true);
  const [imagenes, setImagenes] = useState<ImagenesPreparadas | null>(null);
  const [preparando, setPreparando] = useState(false);
  const [vista, setVista] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = texto.trim() !== '' && (editando || imagenes !== null) && !preparando;

  async function elegir(archivo: File | undefined) {
    if (!archivo) {
      return;
    }
    setError(null);
    setPreparando(true);
    try {
      const preparadas = await prepararImagenes(archivo);
      setImagenes(preparadas);
      setVista(await aUrlDeDatos(preparadas.grande));
    } catch {
      setImagenes(null);
      setVista(null);
      setError('ESE ARCHIVO NO SE PUDO ABRIR COMO IMAGEN. USE PNG, JPEG O WEBP.');
    } finally {
      setPreparando(false);
    }
  }

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }
    setError(null);
    setEnviando(true);
    const enlace = destino === OTRA ? externo.trim() || null : destino || null;
    const datos: DatosDeBanner = {
      texto: texto.trim(),
      enlace,
      orden: banner?.orden ?? siguienteOrden,
      publicado,
      desde: desde || null,
      hasta: hasta || null,
    };
    try {
      if (editando) {
        await api.put<BannerDelPortal>(`/portal/banners/${banner.id}`, datos);
        if (imagenes) {
          const formulario = new FormData();
          formulario.append('imagen', imagenes.grande, nombreDelArchivo(imagenes.grande, 'grande'));
          formulario.append(
            'imagenMovil',
            imagenes.movil,
            nombreDelArchivo(imagenes.movil, 'movil'),
          );
          await api.subir<BannerDelPortal>(`/portal/banners/${banner.id}/imagenes`, formulario);
        }
      } else if (imagenes) {
        const formulario = new FormData();
        formulario.append('texto', datos.texto);
        if (datos.enlace) {
          formulario.append('enlace', datos.enlace);
        }
        formulario.append('orden', String(datos.orden));
        formulario.append('publicado', String(datos.publicado));
        if (datos.desde) {
          formulario.append('desde', datos.desde);
        }
        if (datos.hasta) {
          formulario.append('hasta', datos.hasta);
        }
        formulario.append('imagen', imagenes.grande, nombreDelArchivo(imagenes.grande, 'grande'));
        formulario.append('imagenMovil', imagenes.movil, nombreDelArchivo(imagenes.movil, 'movil'));
        await api.subir<BannerDelPortal>('/portal/banners', formulario);
      }
      alGuardar();
      alCerrar();
      notificar.exito(editando ? 'Banner actualizado' : 'Banner creado');
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el banner';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  const aviso = imagenes ? avisoDeMedidas(imagenes.ancho, imagenes.alto) : null;

  return (
    <Modal
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar banner' : 'Nuevo banner'}
      subtitulo="Se ve en el carrusel de la portada de la ventanilla"
      icono={IconoDeImagen}
      ancho="amplio"
      cerrarAlPulsarFuera={false}
    >
      <form onSubmit={enviar} className="space-y-4">
        {error && (
          <p
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
          >
            {error}
          </p>
        )}

        <Campo
          etiqueta={editando ? 'Cambiar la imagen' : 'Imagen'}
          requerido={!editando}
          ayuda="PNG, JPEG O WEBP. LO IDEAL: 1942 × 809 PÍXELES. SE CONVIERTE A WEBP ANTES DE SUBIRLA."
        >
          <label className="flex cursor-pointer items-center gap-3 rounded-xl border border-dashed border-armazon-300 p-4 transition-colors hover:border-marca-400 hover:bg-marca-50/40">
            <Upload className="size-5 shrink-0 text-armazon-400" aria-hidden />
            <span className="text-sm uppercase text-armazon-600">
              {preparando
                ? 'Preparando la imagen…'
                : imagenes
                  ? `Lista: ${enPeso(imagenes.grande.size)} computadora · ${enPeso(imagenes.movil.size)} teléfono`
                  : 'Elegir un archivo'}
            </span>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="sr-only"
              onChange={(e) => void elegir(e.target.files?.[0])}
            />
          </label>
        </Campo>

        {vista && (
          <div className="overflow-hidden rounded-xl ring-1 ring-armazon-200">
            <img src={vista} alt="Vista previa" className="aspect-[1942/809] w-full object-cover" />
          </div>
        )}
        {aviso && (
          <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs uppercase text-amber-900">
            <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden />
            {aviso}
          </p>
        )}

        <Campo
          etiqueta="Lo que dice el banner"
          requerido
          ayuda="EL TEXTO QUE VA PINTADO EN LA IMAGEN. ES LO QUE ESCUCHA QUIEN NO PUEDE VERLA."
        >
          <input
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className={claseEntrada}
            maxLength={300}
            placeholder="Paga tus servicios en línea: rápido, fácil y seguro."
          />
        </Campo>

        <div className="grid gap-4 sm:grid-cols-2">
          <Campo
            etiqueta="Al tocarlo lleva a"
            ayuda="UNA PÁGINA DE LA VENTANILLA O UNA DIRECCIÓN https"
          >
            <select
              value={destino}
              onChange={(e) => setDestino(e.target.value)}
              className={claseEntrada}
            >
              <option value="">No lleva a ningún sitio</option>
              {PAGINAS_DE_LA_VENTANILLA.map((p) => (
                <option key={p.ruta} value={p.ruta}>
                  {p.nombre}
                </option>
              ))}
              <option value={OTRA}>Otra dirección (https)…</option>
            </select>
          </Campo>
          {destino === OTRA && (
            <Campo etiqueta="Dirección" requerido ayuda="TIENE QUE EMPEZAR POR https://">
              <input
                value={externo}
                onChange={(e) => setExterno(e.target.value)}
                className={claseEntrada}
                placeholder="https://www.epagal.gob.ec"
              />
            </Campo>
          )}
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Campo etiqueta="Desde" ayuda="VACÍO: DESDE YA">
            <input
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
              className={claseEntrada}
            />
          </Campo>
          <Campo etiqueta="Hasta" ayuda="VACÍO: SIN FIN">
            <input
              type="date"
              value={hasta}
              onChange={(e) => setHasta(e.target.value)}
              className={claseEntrada}
            />
          </Campo>
          <div className="flex items-start gap-3 rounded-xl border border-armazon-200 p-3">
            <span className="mt-0.5 shrink-0">
              <Interruptor
                activo={publicado}
                alCambiar={() => setPublicado((v) => !v)}
                etiqueta="Publicado"
              />
            </span>
            <span className="text-sm font-medium uppercase text-armazon-900">Publicado</span>
          </div>
        </div>

        <AccionesDelFormulario
          alCancelar={alCerrar}
          enviando={enviando || !listo}
          textoEnviar={editando ? 'Actualizar banner' : 'Crear banner'}
        />
      </form>
    </Modal>
  );
}

function nombreDelArchivo(blob: Blob, cual: string): string {
  return `banner-${cual}.${blob.type === 'image/webp' ? 'webp' : blob.type === 'image/png' ? 'png' : 'jpg'}`;
}
